/**
 * Global Table Configuration
 *
 * Centralized configuration for table styling and interactions across the application.
 * This ensures consistent hover effects, row selections, and table behaviors.
 */

import type { CSSProperties as ReactCSSProperties } from 'react';

// Allow CSS-in-JS pseudo-selectors and nested rules (e.g. ':hover')
type CSSProperties = ReactCSSProperties & Record<string, any>;

export interface TableConfig {
  /** Base styles for all tables */
  baseStyle: CSSProperties;
  /** Row-specific configurations */
  row: {
    /** Default row styles */
    default: CSSProperties;
    /** Hover effect styles */
    hover: CSSProperties;
    /** Selected/active row styles */
    selected: CSSProperties;
    /** Alternate row styles (striping) */
    alternate: CSSProperties;
    /** Disabled row styles */
    disabled: CSSProperties;
  };
  /** Header configurations */
  header: {
    /** Default header styles */
    default: CSSProperties;
    /** Sortable column hover */
    hover: CSSProperties;
    /** Active sort column */
    sorted: CSSProperties;
  };
  /** Cell configurations */
  cell: {
    /** Default cell padding and spacing */
    default: CSSProperties;
    /** Clickable cell hover */
    clickable: CSSProperties;
  };
  /** Animation configurations */
  animation: {
    /** Transition duration in ms */
    duration: number;
    /** Easing function */
    easing: string;
  };
  /** Interaction configurations */
  interaction: {
    /** Enable row hover effects */
    enableRowHover: boolean;
    /** Enable row selection */
    enableRowSelection: boolean;
    /** Enable cell highlighting on hover */
    enableCellHover: boolean;
    /** Show hover cursor */
    showPointer: boolean;
  };
}

export const tableConfig: TableConfig = {
  // Base table styles
  baseStyle: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    lineHeight: '1.5',
  },

  // Row configurations — uses shadcn CSS variables for theme-aware colors
  row: {
    default: {
      borderBottom: '1px solid hsl(var(--border))',
      transition: 'all 0.2s ease',
    },
    hover: {
      backgroundColor: 'hsl(var(--accent))',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
      transform: 'translateY(-1px)',
      cursor: 'pointer',
    },
    selected: {
      backgroundColor: 'hsl(var(--accent))',
      borderLeft: '3px solid hsl(var(--ring))',
      fontWeight: '500',
    },
    alternate: {
      backgroundColor: 'hsl(var(--muted) / 0.5)',
    },
    disabled: {
      backgroundColor: 'hsl(var(--muted))',
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },

  // Header configurations — uses shadcn CSS variables for theme-aware colors
  header: {
    default: {
      backgroundColor: 'hsl(var(--muted))',
      fontWeight: '600',
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '2px solid hsl(var(--border))',
      color: 'hsl(var(--muted-foreground))',
    },
    hover: {
      backgroundColor: 'hsl(var(--accent))',
      color: 'hsl(var(--accent-foreground))',
      cursor: 'pointer',
    },
    sorted: {
      backgroundColor: 'hsl(var(--accent))',
      color: 'hsl(var(--primary))',
      borderBottom: '2px solid hsl(var(--primary))',
    },
  },

  // Cell configurations
  cell: {
    default: {
      padding: '12px 16px',
      verticalAlign: 'middle',
    },
    clickable: {
      cursor: 'pointer',
      textDecoration: 'underline',
      textDecorationColor: 'transparent',
      transition: 'text-decoration-color 0.2s ease',
      ':hover': {
        textDecorationColor: 'currentColor',
      },
    },
  },

  // Animation settings
  animation: {
    duration: 200,
    easing: 'ease-in-out',
  },

  // Interaction settings
  interaction: {
    enableRowHover: true,
    enableRowSelection: true,
    enableCellHover: false,
    showPointer: true,
  },
};

/**
 * Utility function to get row styles based on state
 */
export function getRowStyles(
  isHovered: boolean = false,
  isSelected: boolean = false,
  isDisabled: boolean = false,
  isAlternate: boolean = false
): CSSProperties {
  const styles = { ...tableConfig.row.default };

  if (isAlternate && !isSelected) {
    Object.assign(styles, tableConfig.row.alternate);
  }

  if (isDisabled) {
    Object.assign(styles, tableConfig.row.disabled);
  }

  if (isSelected) {
    Object.assign(styles, tableConfig.row.selected);
  }

  if (isHovered && !isDisabled && tableConfig.interaction.enableRowHover) {
    Object.assign(styles, tableConfig.row.hover);
  }

  return styles;
}

/**
 * Utility function to get header styles based on state
 */
export function getHeaderStyles(
  isHovered: boolean = false,
  isSorted: boolean = false,
  isSortable: boolean = false
): CSSProperties {
  const styles = { ...tableConfig.header.default };

  if (isSorted) {
    Object.assign(styles, tableConfig.header.sorted);
  }

  if (isHovered && isSortable) {
    Object.assign(styles, tableConfig.header.hover);
  }

  return styles;
}

/**
 * CSS class names for table styling
 * Use these with CSS modules or styled-components
 */
export const tableClasses = {
  table: 'app-table',
  row: 'app-table-row',
  rowHover: 'app-table-row--hover',
  rowSelected: 'app-table-row--selected',
  rowDisabled: 'app-table-row--disabled',
  header: 'app-table-header',
  headerSortable: 'app-table-header--sortable',
  headerSorted: 'app-table-header--sorted',
  cell: 'app-table-cell',
  cellClickable: 'app-table-cell--clickable',
} as const;

/**
 * Hook-friendly table interaction handlers
 */
export const tableHandlers = {
  /**
   * Handle row click with selection
   */
  handleRowClick: (
    rowId: string | number,
    onSelect?: (id: string | number) => void
  ) => {
    if (tableConfig.interaction.enableRowSelection && onSelect) {
      onSelect(rowId);
    }
  },

  /**
   * Handle header click for sorting
   */
  handleHeaderClick: (
    column: string,
    onSort?: (column: string) => void
  ) => {
    if (onSort) {
      onSort(column);
    }
  },
};

/**
 * Responsive table configurations
 */
export const responsiveTableConfig = {
  breakpoints: {
    mobile: 640,
    tablet: 768,
    desktop: 1024,
  },
  mobileStyles: {
    fontSize: '12px',
    padding: '8px',
  },
  tabletStyles: {
    fontSize: '13px',
    padding: '10px',
  },
} as const;